import React, { useEffect, useState } from "react";
import { Sparkles, Bug, Lightbulb, MessageSquare, Trash2, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CAT_ICON = { feature: Sparkles, bug: Bug, improvement: Lightbulb, other: MessageSquare };
const STATUS_OPTIONS = ["new", "under_review", "planned", "in_progress", "done", "declined"];
const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  planned: "bg-violet-50 text-violet-700 border-violet-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/feature-requests");
    setItems(data);
    setDrafts(Object.fromEntries(data.map((i) => [i.id, { status: i.status, admin_note: i.admin_note || "" }])));
  };

  useEffect(() => { load(); }, []);

  const save = async (id) => {
    const d = drafts[id];
    await api.patch(`/admin/feature-requests/${id}`, d);
    toast.success("Saved");
    await load();
  };

  const remove = async (id) => {
    if (!confirm("Delete this request?")) return;
    await api.delete(`/admin/feature-requests/${id}`);
    await load();
  };

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <p className="eyebrow mb-2">Product feedback</p>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-8" data-testid="admin-feedback-title">Feature Requests</h1>

      <div className="space-y-4" data-testid="admin-feedback-list">
        {items.length === 0 ? (
          <div className="bento p-12 text-center text-slate-500">
            <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            No feature requests yet. Agencies will see this in their dashboard soon.
          </div>
        ) : (
          items.map((r) => {
            const Icon = CAT_ICON[r.category] || MessageSquare;
            const d = drafts[r.id] || { status: r.status, admin_note: r.admin_note };
            const dirty = d.status !== r.status || (d.admin_note || "") !== (r.admin_note || "");
            return (
              <div key={r.id} className={`bento p-6 border-l-4 ${STATUS_STYLES[r.status]?.split(" ").find((c) => c.startsWith("border-")) || "border-slate-200"}`} data-testid={`admin-feedback-${r.id}`}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-slate-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-slate-900 text-lg">{r.title}</h3>
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full font-mono text-slate-600">{r.category}</span>
                    </div>
                    <p className="text-sm text-slate-500 font-mono mb-3">
                      {r.agency_name} · {r.submitted_by_email} · {new Date(r.created_at).toLocaleDateString()}
                    </p>
                    {r.description && <p className="text-sm text-slate-700 whitespace-pre-wrap mb-4">{r.description}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start mt-4">
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Status</label>
                        <select value={d.status} onChange={(e) => setDraft(r.id, { status: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid={`feedback-status-${r.id}`}>
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Internal / public note (visible to agency)</label>
                        <input value={d.admin_note} onChange={(e) => setDraft(r.id, { admin_note: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm" placeholder="Share a quick update with the agency…" data-testid={`feedback-note-${r.id}`} />
                      </div>
                    </div>
                  </div>
                  <button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 shrink-0" data-testid={`delete-feedback-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {dirty && (
                  <div className="flex justify-end">
                    <button onClick={() => save(r.id)} className="inline-flex items-center gap-1 text-sm bg-slate-900 text-white rounded-full px-4 py-2 font-medium hover:bg-slate-700 active:scale-95" data-testid={`save-feedback-${r.id}`}>
                      <Save className="w-3.5 h-3.5" /> Save changes
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
